import React, { useState } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { Menu, Plus, AlertTriangle } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { useAuth } from '../../auth/AuthContext';

const ROUTE_LABELS = {
  '/dashboard':        'Dashboard',
  '/quotes':           'Quotes',
  '/quotes/new':       'New Quote',
  '/contracts':        'Contracts',
  '/customers':        'Partners',
  '/tenants':          'Tenants',
  '/reports':          'Reports',
  '/admin/users':      'User Management',
  '/admin/pricing':    'Pricing Configuration',
  '/admin/settings':   'Settings',
  '/admin/audit':      'Audit Log',
  '/admin/fx-rates':   'FX Rates',
  '/admin/currencies': 'Currencies',
  '/admin/extras':     'Extras Catalogue',
};

export function Header({ onMenuClick }) {
  const location   = useLocation();
  const navigate   = useNavigate();
  const { user }   = useAuth();
  const isAccounts = user?.role === 'ACCOUNTS';
  const pageTitle  = ROUTE_LABELS[location.pathname] || 'C4P Quoting Portal';
  const initials   = user?.name ? user.name.split(' ').map(p => p[0]).join('').slice(0, 2).toUpperCase() : 'U';

  const [showConfirm, setShowConfirm] = useState(false);

  // Intercept if already inside the quote wizard (new quote or edit quote)
  const onNewQuoteClick = (e) => {
    if (location.pathname === '/quotes/new' || /\/quotes\/.+\/edit/.test(location.pathname)) {
      e.preventDefault();
      setShowConfirm(true);
    }
    // Otherwise the Link navigates normally
  };

  const handleConfirmReset = () => {
    setShowConfirm(false);
    if (/\/quotes\/.+\/edit/.test(location.pathname)) {
      // Editing an existing quote — navigate to the new-quote wizard directly
      navigate('/quotes/new');
    } else {
      // Already on /quotes/new — fire the reset event so the wizard clears in place
      window.dispatchEvent(new CustomEvent('reset-quote-wizard'));
    }
  };

  return (
    <>
      <header
        className="flex items-center h-14 px-4 md:px-6 gap-4 flex-shrink-0 bg-white"
        style={{ borderBottom: '1px solid hsl(var(--border))', zIndex: 10 }}
      >
        <button
          className="lg:hidden p-2 rounded-md"
          onClick={onMenuClick}
          aria-label="Open menu"
          style={{ color: 'hsl(var(--muted-fg))' }}
        >
          <Menu size={20} />
        </button>

        <div className="flex-1 min-w-0">
          <h1
            className="text-[15px] font-bold truncate"
            style={{ fontFamily: 'var(--font-display)', color: 'hsl(var(--fg))' }}
            data-testid="header-page-title"
          >
            {pageTitle}
          </h1>
        </div>

        <div className="flex items-center gap-3">
          {!isAccounts && (
          <Link to="/quotes/new" onClick={onNewQuoteClick}>
            <Button
              size="sm"
              className="hidden sm:flex items-center gap-1.5 font-bold"
              style={{
                background: 'hsl(var(--c4p-brand-gold))',
                color: '#1a1a1a',
                fontSize: '13px',
                height: '34px',
                paddingLeft: '14px',
                paddingRight: '14px',
              }}
              data-testid="header-new-quote-button"
            >
              <Plus size={14} strokeWidth={2.5} />
              New Quote
            </Button>
          </Link>
          )}

          <div
            className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold cursor-pointer select-none flex-shrink-0"
            style={{
              background: 'hsl(var(--c4p-brand-blue))',
              color: 'hsl(218 52% 12%)',
              letterSpacing: '-0.02em',
            }}
            title={`${user?.name} \u00b7 ${user?.role}`}
            data-testid="header-user-avatar"
          >
            {initials}
          </div>
        </div>
      </header>

      {/* Confirm reset dialog — only shown when already in the wizard */}
      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle size={18} style={{ color: '#d97706' }} />
              Start a new quote?
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>
            All current quote information will be lost. Are you sure you want to clear everything and start again from scratch?
          </p>
          <DialogFooter className="mt-2 gap-2">
            <Button variant="outline" onClick={() => setShowConfirm(false)} data-testid="confirm-keep-quote">
              No, keep my quote
            </Button>
            <Button
              onClick={handleConfirmReset}
              style={{ background: '#d97706', color: 'white' }}
              data-testid="confirm-reset-quote"
            >
              Yes, start again
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
